import React, { useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Flex,
  Text,
  Badge,
  Icon,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiFileText, FiTrash2, FiZap } from 'react-icons/fi';
import { useCampaignContext } from './CampaignContext';
import {
  ICON_MAP,
  COLOR_MAP_HEX as COLOR_MAP,
  getSummary,
} from '../../../../components/CRM/Campaigns/campaignConstants';

function AutomationNode({ data }) {
  const { node, onNodeClick, onRemoveNode, testResult, isTestRunning, onTestNode, hasTestContact } = data;
  const borderColor = testResult?.status === 'success' ? '#38A169'
    : testResult?.status === 'error' ? '#E53E3E'
    : testResult?.status === 'soft_failed' ? '#DD6B20'
    : testResult?.status === 'would_skip' ? '#D69E2E'
    : (COLOR_MAP[node.subType] || '#A0AEC0');
  const NodeIcon = ICON_MAP[node.subType] || FiFileText;
  const glowShadow = testResult ? `0 0 12px ${borderColor}40` : undefined;

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={3}
      minW="220px"
      maxW="280px"
      cursor="pointer"
      boxShadow={glowShadow}
      onClick={() => onNodeClick?.(node)}
      _hover={{ shadow: 'lg' }}
      transition="all 0.15s"
      _dark={{ bg: 'gray.700' }}
    >
      <Flex align="center" gap={2} mb={1}>
        <Flex
          align="center"
          justify="center"
          w={7}
          h={7}
          borderRadius="md"
          bg={`${borderColor}20`}
        >
          <Icon as={NodeIcon} boxSize={4} color={borderColor} />
        </Flex>
        <Badge fontSize="10px" colorScheme="gray" textTransform="uppercase">
          {node.subType.replace(/_/g, ' ')}
        </Badge>
        <IconButton
          icon={<FiTrash2 />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          ml="auto"
          onClick={(e) => { e.stopPropagation(); onRemoveNode?.(node.id); }}
          aria-label="Remove"
        />
      </Flex>
      <Text fontSize="xs" color="gray.500" noOfLines={1}>
        {getSummary(node)}
      </Text>
      {testResult && (
        <Text fontSize="9px" color={borderColor} mt={1} noOfLines={1} fontWeight="medium">
          {testResult.output?.summary || 'Tested'}
        </Text>
      )}
    </Box>
  );
}

function TriggerNode({ data }) {
  return (
    <Box
      bg="blue.50"
      border="2px solid"
      borderColor="blue.400"
      borderRadius="xl"
      p={3}
      minW="220px"
      _dark={{ bg: 'blue.900' }}
    >
      <Flex align="center" gap={2}>
        <Icon as={FiZap} boxSize={5} color="blue.500" />
        <Text fontWeight="bold" fontSize="sm">Trigger</Text>
      </Flex>
      <Text fontSize="xs" color="gray.500" mt={1}>
        {data.triggerLabel || 'Manual Add'}
      </Text>
    </Box>
  );
}

const nodeTypes = {
  automationNode: AutomationNode,
  triggerNode: TriggerNode,
};

export default function CanvasBuilder({ onNodeClick }) {
  const { template, nodes, removeNode, testResults, testRunning, testNode, testContact } = useCampaignContext();

  // Convert internal node list into React Flow nodes and edges
  const { flowNodes, flowEdges } = useMemo(() => {
    const fNodes = [];
    const fEdges = [];
    const Y_GAP = 120;
    const X_CENTER = 300;

    // Trigger node
    fNodes.push({
      id: 'trigger',
      type: 'triggerNode',
      position: { x: X_CENTER - 110, y: 0 },
      data: { triggerLabel: template?.triggerType?.replace(/_/g, ' ') || 'Manual Add' },
      draggable: true,
    });

    if (nodes.length > 0) {
      fEdges.push({
        id: 'trigger-to-first',
        source: 'trigger',
        target: nodes[0].id,
        animated: true,
        style: { stroke: '#3182CE', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3182CE' },
      });
    }

    // Collect false-branch node IDs for offset positioning
    const falseBranchIds = new Set();
    const nodeMap = {};
    nodes.forEach(n => { nodeMap[n.id] = n; });
    nodes.forEach(n => {
      if ((n.subType === 'IF_ELSE' || n.subType === 'AB_SPLIT' || n.subType === 'WAIT_FOR') && n.falseNodeId) {
        let cid = n.falseNodeId;
        const visited = new Set();
        while (cid && nodeMap[cid] && !visited.has(cid)) {
          visited.add(cid);
          falseBranchIds.add(cid);
          cid = nodeMap[cid].nextNodeId;
        }
      }
    });

    let mainY = 0;
    let falseY = 0;

    nodes.forEach((node, idx) => {
      const color = COLOR_MAP[node.subType] || '#A0AEC0';
      const isFalseBranch = falseBranchIds.has(node.id);
      let x, y;

      if (isFalseBranch) {
        falseY++;
        x = X_CENTER + 150;
        y = falseY * Y_GAP;
      } else {
        mainY++;
        x = X_CENTER - 110;
        y = mainY * Y_GAP;
        falseY = mainY;
      }

      fNodes.push({
        id: node.id,
        type: 'automationNode',
        position: { x, y },
        data: {
          node, onNodeClick, onRemoveNode: removeNode,
          testResult: testResults?.[node.id],
          isTestRunning: testRunning === node.id,
          onTestNode: testNode,
          hasTestContact: !!testContact,
        },
        draggable: true,
      });

      if (node.nextNodeId) {
        const isBranching = node.subType === 'IF_ELSE' || node.subType === 'AB_SPLIT' || node.subType === 'WAIT_FOR';
        const trueLabel = node.subType === 'WAIT_FOR' ? 'Met' : 'True';
        fEdges.push({
          id: `${node.id}-to-${node.nextNodeId}`,
          source: node.id,
          target: node.nextNodeId,
          animated: true,
          label: isBranching ? trueLabel : undefined,
          style: { stroke: isBranching ? '#38A169' : color, strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: isBranching ? '#38A169' : color },
        });
      }

      if ((node.subType === 'IF_ELSE' || node.subType === 'AB_SPLIT' || node.subType === 'WAIT_FOR') && node.falseNodeId) {
        const falseLabel = node.subType === 'WAIT_FOR' ? 'Timeout' : 'False';
        fEdges.push({
          id: `${node.id}-false-to-${node.falseNodeId}`,
          source: node.id,
          target: node.falseNodeId,
          animated: true,
          label: falseLabel,
          style: { stroke: '#E53E3E', strokeWidth: 2, strokeDasharray: '5,5' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#E53E3E' },
        });
      }
    });

    return { flowNodes: fNodes, flowEdges: fEdges };
  }, [template, nodes, onNodeClick, removeNode, testResults, testRunning, testNode, testContact]);

  const [rfNodes, setRfNodes, onNodesChange] = useNodesState(flowNodes);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Keep nodes in sync when campaign nodes change
  React.useEffect(() => {
    setRfNodes(flowNodes);
    setRfEdges(flowEdges);
  }, [flowNodes, flowEdges, setRfNodes, setRfEdges]);

  const bgColor = useColorModeValue('#f7fafc', '#1a202c');
  const gridColor = useColorModeValue('#E2E8F0', '#2D3748');

  return (
    <Box w="100%" h="600px" borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200" _dark={{ borderColor: 'gray.600' }}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        style={{ background: bgColor }}
        minZoom={0.3}
        maxZoom={2}
      >
        <Background color={gridColor} gap={20} />
        <Controls />
        <MiniMap
          nodeStrokeWidth={3}
          style={{ height: 80, width: 120 }}
        />
        <Panel position="top-right">
          <Badge colorScheme="purple" variant="subtle" px={2} py={1}>
            Advanced Mode
          </Badge>
        </Panel>
      </ReactFlow>
    </Box>
  );
}
